const SUPPORTED_MIMES = [
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
];

const MIME_TO_EXT = {
    'audio/mp4': 'mp4',
    'audio/webm': 'webm',
    'audio/ogg': 'ogg',
};

const ERROR_MESSAGES = {
    NotAllowedError: 'Brak dostępu do mikrofonu.',
    PermissionDeniedError: 'Brak dostępu do mikrofonu.',
    NotFoundError: 'Nie wykryto mikrofonu.',
    NotReadableError: 'Mikrofon jest używany przez inną aplikację.',
    SecurityError: 'Nagrywanie wymaga połączenia HTTPS.',
    AbortError: 'Nagrywanie zostało przerwane.',
    NotSupportedError: 'Twoja przeglądarka nie obsługuje nagrywania.',
};

const FALLBACK_ERROR = 'Nie udało się uzyskać dostępu do mikrofonu.';

export function pickSupportedMime() {
    if (typeof MediaRecorder === 'undefined') return null;
    for (const mime of SUPPORTED_MIMES) {
        if (MediaRecorder.isTypeSupported(mime)) return mime;
    }
    return null;
}

export function mimeToExtension(mime) {
    const base = (mime || '').split(';')[0].trim();
    return MIME_TO_EXT[base] || 'webm';
}

export function getRecorderErrorMessage(err) {
    return ERROR_MESSAGES[err?.name] || FALLBACK_ERROR;
}

export class AudioRecorder {
    constructor() {
        this.stream = null;
        this.recorder = null;
        this.chunks = [];
        this.mimeType = null;
        this.startedAt = null;
        this.autoStopTimer = null;
        this.tickTimer = null;
        this.maxSeconds = 30;
        this.onTick = null;
        this.onAutoStop = null;
        this.onError = null;
        this.audioContext = null;
        this.analyser = null;
        this.visualizerCanvas = null;
        this.visualizerData = null;
        this.visualizerRaf = null;
        this.visualizerColor = '#D20007';
    }

    static isSupported() {
        return typeof MediaRecorder !== 'undefined'
            && !!navigator.mediaDevices?.getUserMedia
            && pickSupportedMime() !== null;
    }

    async requestPermission() {
        if (!AudioRecorder.isSupported()) {
            const err = new Error('MediaRecorder not supported');
            err.name = 'NotSupportedError';
            throw err;
        }
        this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        return this.stream;
    }

    hasStream() {
        return Boolean(this.stream && this.stream.getTracks().some((t) => t.readyState === 'live'));
    }

    start({ maxSeconds = 30, onTick, onAutoStop, onError } = {}) {
        if (!this.hasStream()) {
            throw new Error('No active stream — call requestPermission first');
        }
        this.mimeType = pickSupportedMime();
        this.chunks = [];
        this.maxSeconds = maxSeconds;
        this.onTick = onTick;
        this.onAutoStop = onAutoStop;
        this.onError = onError;

        const opts = this.mimeType ? { mimeType: this.mimeType } : undefined;
        try {
            this.recorder = new MediaRecorder(this.stream, opts);
        } catch (e) {
            this.recorder = new MediaRecorder(this.stream);
            this.mimeType = this.recorder.mimeType || 'audio/webm';
        }

        this.recorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) this.chunks.push(e.data);
        };
        this.recorder.onerror = (e) => {
            const err = e?.error || new Error('Recording error');
            if (this.onError) this.onError(err);
            this.cleanup();
        };

        this.startedAt = Date.now();
        this.recorder.start(1000);

        this.tickTimer = setInterval(() => {
            const elapsed = (Date.now() - this.startedAt) / 1000;
            if (this.onTick) this.onTick(elapsed);
        }, 100);

        this.autoStopTimer = setTimeout(() => {
            this.stop()
                .then((result) => {
                    if (result && this.onAutoStop) this.onAutoStop(result);
                })
                .catch(() => {});
        }, this.maxSeconds * 1000);
    }

    stop() {
        return new Promise((resolve, reject) => {
            if (!this.recorder || this.recorder.state === 'inactive') {
                this.cleanup();
                resolve(null);
                return;
            }
            this.recorder.onstop = () => {
                const elapsed = (Date.now() - this.startedAt) / 1000;
                const mime = this.mimeType || 'audio/webm';
                const blob = new Blob(this.chunks, { type: mime });
                this.cleanup();
                resolve({
                    blob,
                    duration: elapsed,
                    mimeType: mime,
                    extension: mimeToExtension(mime),
                });
            };
            try {
                this.recorder.stop();
            } catch (e) {
                this.cleanup();
                reject(e);
            }
        });
    }

    cancel() {
        if (this.recorder && this.recorder.state !== 'inactive') {
            try {
                this.recorder.onstop = null;
                this.recorder.stop();
            } catch (e) {}
        }
        this.chunks = [];
        this.cleanup();
    }

    releaseStream() {
        if (this.stream) {
            this.stream.getTracks().forEach((t) => t.stop());
            this.stream = null;
        }
    }

    attachVisualizer(canvas, color) {
        if (!canvas || !this.stream) return;
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        try {
            this.audioContext = new Ctx();
            const source = this.audioContext.createMediaStreamSource(this.stream);
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 256;
            this.analyser.smoothingTimeConstant = 0.6;
            source.connect(this.analyser);
            this.visualizerData = new Uint8Array(this.analyser.frequencyBinCount);
            this.visualizerCanvas = canvas;
            if (color) this.visualizerColor = color;
            this.drawVisualizerFrame();
        } catch (e) {
            this.detachVisualizer();
        }
    }

    drawVisualizerFrame() {
        if (!this.analyser || !this.visualizerCanvas) return;
        this.visualizerRaf = requestAnimationFrame(() => this.drawVisualizerFrame());
        this.analyser.getByteFrequencyData(this.visualizerData);

        const canvas = this.visualizerCanvas;
        const ctx = canvas.getContext('2d');
        const w = canvas.width;
        const h = canvas.height;
        ctx.clearRect(0, 0, w, h);
        ctx.fillStyle = this.visualizerColor;

        const bars = 48;
        const step = Math.floor(this.visualizerData.length / bars) || 1;
        const gap = 2;
        const barWidth = Math.max(2, (w - gap * (bars - 1)) / bars);
        let x = 0;
        for (let i = 0; i < bars; i++) {
            const value = this.visualizerData[i * step] / 255;
            const barHeight = Math.max(2, value * h);
            ctx.fillRect(x, (h - barHeight) / 2, barWidth, barHeight);
            x += barWidth + gap;
        }
    }

    detachVisualizer() {
        if (this.visualizerRaf) {
            cancelAnimationFrame(this.visualizerRaf);
            this.visualizerRaf = null;
        }
        if (this.audioContext) {
            this.audioContext.close().catch(() => {});
            this.audioContext = null;
        }
        if (this.visualizerCanvas) {
            const ctx = this.visualizerCanvas.getContext('2d');
            ctx?.clearRect(0, 0, this.visualizerCanvas.width, this.visualizerCanvas.height);
        }
        this.analyser = null;
        this.visualizerData = null;
        this.visualizerCanvas = null;
    }

    cleanup() {
        if (this.tickTimer) {
            clearInterval(this.tickTimer);
            this.tickTimer = null;
        }
        if (this.autoStopTimer) {
            clearTimeout(this.autoStopTimer);
            this.autoStopTimer = null;
        }
        this.detachVisualizer();
        this.releaseStream();
    }
}
